/**
 * Typed TanStack navigate targets. Origin screens shape the input; this
 * module applies the one write rule so a hop cannot pin tax=sector (D3).
 */
import type { AreaId, SectorId, Ticker } from '@meridian/contracts';

import { chartStudySearch, type ChartStudySearch } from './chart-studies';
import { writtenNavSearch, type NavSearchInput, type WrittenNavSearch } from './search';

export function stockTarget(
  ticker: Ticker,
  search: NavSearchInput,
): {
  readonly to: '/stock/$ticker';
  readonly params: { readonly ticker: Ticker };
  readonly search: WrittenNavSearch;
} {
  return {
    to: '/stock/$ticker',
    params: { ticker },
    search: writtenNavSearch(search),
  };
}

export function chartTarget(
  ticker: Ticker,
  search: ChartStudySearch,
): {
  readonly to: '/chart/$ticker';
  readonly params: { readonly ticker: Ticker };
  readonly search: ReturnType<typeof chartStudySearch>;
} {
  return {
    to: '/chart/$ticker',
    params: { ticker },
    search: chartStudySearch(search),
  };
}

export function technicalTarget(
  ticker: Ticker,
  search: ChartStudySearch,
): {
  readonly to: '/technical/$ticker';
  readonly params: { readonly ticker: Ticker };
  readonly search: ReturnType<typeof chartStudySearch>;
} {
  return {
    to: '/technical/$ticker',
    params: { ticker },
    search: chartStudySearch(search),
  };
}

export function sectorTarget(
  sectorId: SectorId,
  search: Pick<NavSearchInput, 'win' | 'idx' | 'book' | 'cmp'>,
): {
  readonly to: '/sector/$sectorId';
  readonly params: { readonly sectorId: SectorId };
  readonly search: WrittenNavSearch;
} {
  return {
    to: '/sector/$sectorId',
    params: { sectorId },
    search: writtenNavSearch({
      win: search.win,
      idx: search.idx,
      book: search.book,
      cmp: search.cmp,
    }),
  };
}

export function areaTarget(
  areaId: AreaId,
  search: Pick<NavSearchInput, 'win' | 'idx' | 'book' | 'cmp'>,
): {
  readonly to: '/area/$areaId';
  readonly params: { readonly areaId: AreaId };
  readonly search: WrittenNavSearch & { readonly tax: 'industry' };
} {
  const written = writtenNavSearch({
    win: search.win,
    idx: search.idx,
    tax: 'industry',
    book: search.book,
    cmp: search.cmp,
  });
  return {
    to: '/area/$areaId',
    params: { areaId },
    search: { ...written, tax: 'industry' },
  };
}

export function dashboardTarget(
  search: Pick<NavSearchInput, 'win' | 'idx' | 'tax' | 'book' | 'cmp'>,
): {
  readonly to: '/';
  readonly search: WrittenNavSearch;
} {
  return {
    to: '/',
    search: writtenNavSearch({
      win: search.win,
      idx: search.idx,
      tax: search.tax,
      book: search.book,
      cmp: search.cmp,
    }),
  };
}

/** Area crumb — only when the URL carried industry tax and a valid area. */
export function areaBackTarget(search: NavSearchInput): ReturnType<typeof areaTarget> | undefined {
  if (search.tax !== 'industry' || search.area === undefined) return undefined;
  return areaTarget(search.area, search);
}
